import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { api, apiErr } from '@/lib/api';

const summaryClass = 'flex min-h-11 cursor-pointer items-center gap-2 rounded-md py-2 font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring';
const sourceLabels = {
  verified_posting: 'Exact posting previously verified',
  verified_program: 'Program intake previously verified',
  restricted: 'Access restricted at last check',
  unavailable: 'Posting unavailable at last check',
  source_unavailable: 'Source unavailable at last check',
  unsupported_target: 'Exact vacancy unverified',
};
const kinds = { responsibility: 'Responsibility', required: 'Qualification', preferred: 'Preference' };
const evidenceSections = { Employment: 'employments', Achievement: 'achievements', Skill: 'skills', Project: 'projects', Education: 'education', Certification: 'certifications' };
const coverage = {
  supported: 'Recorded evidence', related: 'Related evidence', gap: 'Material evidence gap',
  eligibility: 'Eligibility to confirm', unmapped: 'Evidence link pending',
};
const dateOnly = (value) => value?.slice(0, 10);

function Wording({ label, value }) {
  return <div><dt className="font-medium">{label}</dt><dd className="whitespace-pre-wrap text-muted-foreground">{value || 'Unavailable for this section'}</dd></div>;
}

function Evidence({ rows }) {
  if (!rows?.length) return null;
  return <ul className="space-y-2">
    {rows.map((row) => <li key={row.id}>
      <Link to={`/career?section=${evidenceSections[row.kind]}&evidence=${row.id}`} className="inline-flex min-h-11 items-center text-primary underline">
        {row.label}
      </Link>
      <p className="text-muted-foreground">{row.quote}</p>
    </li>)}
  </ul>;
}

function Proposal({ item, inputToken, canReview, onSave }) {
  const decision = item.decision;
  const currentDecision = decision?.input_token === inputToken;
  const [text, setText] = useState(currentDecision && decision?.status === 'approved' ? decision.text : item.text);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const save = async (action) => {
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await onSave(item, { action, text, confirmed, input_token: inputToken, revision: decision?.revision });
      setConfirmed(false);
      setSuccess(action === 'approve' ? 'Wording approved for review. The résumé version is unchanged.' : 'Proposal dismissed.');
    } catch (error) {
      setError(apiErr(error));
    } finally {
      setBusy(false);
    }
  };
  return <details className="border-t border-border">
    <summary className={summaryClass}>{item.target_label} · {currentDecision ? (decision.status === 'approved' ? 'Approved wording' : 'Dismissed') : 'Awaiting owner review'}</summary>
    <div className="space-y-3 pb-4">
      <blockquote className="border-l-2 border-border pl-3">{item.source_excerpt}</blockquote>
      <p>{item.note}</p>
      <Evidence rows={item.evidence} />
      <dl className="grid gap-3 sm:grid-cols-2">
        <Wording label="Original résumé" value={item.original} />
        <Wording label="Current tailored résumé" value={item.current} />
      </dl>
      {decision && !currentDecision && <p>Previous decision · {decision.status} on {dateOnly(decision.at)}. It refers to earlier inputs.</p>}
      {canReview ? <>
        <label className="block font-medium">Proposed wording
          <textarea className="mt-2 min-h-24 w-full rounded-md border border-input bg-background p-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-70"
            value={text} disabled={busy} maxLength={1600} onChange={(event) => { setText(event.target.value); setConfirmed(false); }} />
        </label>
        <label className="flex min-h-11 cursor-pointer items-center gap-3">
          <input className="size-5 shrink-0 accent-primary" type="checkbox" checked={confirmed} disabled={busy}
            onChange={(event) => setConfirmed(event.target.checked)} />
          This wording accurately describes my experience.
        </label>
        {error && <p role="alert" className="text-destructive">{error}</p>}
        {success && <p role="status">{success}</p>}
        <div className="flex flex-wrap gap-2" aria-busy={busy}>
          <Button className="min-h-11" disabled={busy || !confirmed || !text.trim()} onClick={() => save('approve')}>Approve wording</Button>
          <Button className="min-h-11" variant="outline" disabled={busy} onClick={() => save('dismiss')}>Dismiss proposal</Button>
        </div>
      </> : <dl><Wording label="Proposed wording" value={text} /></dl>}
    </div>
  </details>;
}

export default function ResumeSuggestions({ jobId, onSaved, refreshKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let live = true;
    setError('');
    api.get(`/jobs/${jobId}/resume-suggestions`)
      .then(({ data }) => { if (live) setData(data); })
      .catch((error) => { if (live) setError(apiErr(error)); });
    return () => { live = false; };
  }, [jobId, refreshKey, retry]);
  const onSave = async (item, payload) => {
    const result = await api.post(`/jobs/${jobId}/resume-suggestions/${item.id}`, payload);
    setData((current) => ({ ...current, items: current.items.map((row) => row.id === item.id ? { ...row, decision: result.data.decision } : row) }));
    onSaved?.();
  };
  const analysis = data?.analysis;
  const source = analysis?.source;
  return <section aria-labelledby="opportunity-review-title" data-testid="resume-suggestions"
    className="min-w-0 space-y-4 rounded-lg border border-border bg-card p-4 text-sm [overflow-wrap:anywhere]">
    <h3 id="opportunity-review-title" className="text-base font-semibold">Posting evidence and résumé review</h3>
    {error && <div role="alert" className="space-y-2"><p className="text-destructive">{error}</p>
      <Button variant="outline" className="min-h-11" onClick={() => setRetry((n) => n + 1)}>Retry loading review</Button></div>}
    {!data && !error && <p role="status" className="text-muted-foreground">Loading posting evidence and résumé review…</p>}
    {analysis && <>
      <p className="text-muted-foreground" role="status">
        {data.saved ? `Saved analysis · ${dateOnly(analysis.checked_at)}` : data.had_saved ? 'Earlier analysis is out of date · Showing a preview for the current inputs' : 'Current evidence preview · Analysis has yet to be saved'}
        {analysis.inputs.job_status === 'archived' && ' · Archived opportunity'}
      </p>
      <dl className="grid gap-3 sm:grid-cols-2">
        <Wording label="Posting and capture" value={`${sourceLabels[source.status] || 'Exact posting unverified'}. ${source.capture_verified ? `Capture checked ${dateOnly(source.captured_at) || 'at an unknown date'}.` : 'Exact visual capture not established.'}`} />
        <Wording label="Eligibility" value={source.eligibility_hold ? `Eligibility hold · ${source.terms.join(', ') || 'Confirm stated conditions'}` : 'Confirm individual screening conditions with the employer.'} />
        <Wording label="Public application availability" value={source.historical ? 'Current availability unknown. Source check is historical.' : source.availability || 'Application entry not established by this review.'} />
        <Wording label="Hiring prospects" value="This review assesses evidence. Hiring probability is undetermined." />
      </dl>
      {source.reason && <p className="text-muted-foreground">{source.reason}</p>}
      {analysis.limits.length > 0 && <div><h4 className="font-semibold">Evidence limits</h4>
        <ul className="mt-1 list-disc space-y-1 pl-5">{analysis.limits.map((limit) => <li key={limit}>{limit}</li>)}</ul></div>}
      {analysis.requirements.length > 0 && <details>
        <summary className={summaryClass}>Employer requirements and career evidence ({analysis.requirements.length})</summary>
        {analysis.requirements.map((row) => <details key={row.id} className="border-b border-border">
          <summary className={summaryClass}>{kinds[row.kind]} · {coverage[row.coverage]} · {row.excerpt}</summary>
          <p className="pb-2 text-muted-foreground">{row.note}</p>
          <Evidence rows={row.evidence} />
        </details>)}
      </details>}
      <div className="space-y-2">
        <h4 className="font-semibold">Proposed wording for owner review</h4>
        {data.items.length ? data.items.map((item) => <Proposal key={`${data.input_token}:${item.id}`} item={item}
          inputToken={data.input_token} canReview={data.can_review} onSave={onSave} />) :
          <p className="text-muted-foreground">{analysis.requirements.length ? 'This review has no actionable wording proposals.' : 'Wording proposals require usable posting and résumé evidence.'}</p>}
      </div>
      {!!data.existing.length && <details className="border-t border-border">
        <summary className={summaryClass}>Recorded tailoring and changes already in the résumé</summary>
        {data.existing.map((item) => <div key={item.target} className="space-y-2 border-t border-border py-3">
          <h5 className="font-medium">{item.label || item.target} · {item.in_resume ? 'In current résumé' : 'Earlier wording record'}</h5>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Wording label="Original" value={item.original} />
            <Wording label="Current" value={item.current} />
          </dl>
          {item.source_excerpt && <blockquote className="border-l-2 border-border pl-3">{item.source_excerpt}</blockquote>}
          {item.evidence && <p>{item.evidence}</p>}
        </div>)}
      </details>}
      {(data.prior.length > 0 || Object.keys(data.legacy_decisions).length > 0) && <details className="border-t border-border">
        <summary className={summaryClass}>Earlier review decisions</summary>
        {[...data.prior, ...Object.entries(data.legacy_decisions).map(([id, row]) => ({ id, ...row }))].map((row) =>
          <p key={row.id} className="py-2">{row.status} · {dateOnly(row.at)} · {row.text || row.source_excerpt}</p>)}
      </details>}
    </>}
  </section>;
}
